import React, { useState, useEffect, useRef } from 'react';

import { Box, Typography } from '@mui/joy';

import { SiflyWebRTCReader } from './SiflyWebRTCReader';
import VideoScaler, { FitMode } from './VideoScaler';

interface SimpleVideoStreamProps {
  url: string;
  width?: string | number;
  height?: string | number;
  className?: string;
  inPipMode?: boolean;
  fitMode?: FitMode;
  onFitModeChange?: (mode: FitMode) => void;
}

const SimpleVideoStream: React.FC<SimpleVideoStreamProps> = ({
  url,
  width = '100%',
  height = '100%',
  className = '',
  inPipMode = false,
  fitMode: externalFitMode,
  onFitModeChange
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [internalFitMode, setInternalFitMode] = useState<FitMode>('fitWidth');

  // Use external fitMode if provided, otherwise use internal state
  const fitMode = externalFitMode ?? internalFitMode;

  const handleFitModeChange = (mode: FitMode) => {
    if (onFitModeChange) {
      onFitModeChange(mode);
    } else {
      setInternalFitMode(mode);
    }
  };
  const [isInPip, setIsInPip] = useState(false);
  const [showScaleControls, setShowScaleControls] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const webrtcReaderRef = useRef<SiflyWebRTCReader | null>(null);
  const previousUrlRef = useRef<string>('');
  const currentStreamIdRef = useRef<string | null>(null);
  const isRetryingRef = useRef<boolean>(false);
  const isLoadingRef = useRef<boolean>(true);

  // Keep refs in sync with state for use in event handlers
  useEffect(() => {
    isRetryingRef.current = isRetrying;
    isLoadingRef.current = isLoading;
  }, [isRetrying, isLoading]);

  const handleVideoPlaying = () => {
    // Use refs to avoid stale closure issues
    if (isLoadingRef.current || isRetryingRef.current) {
      setIsLoading(false);
      setIsRetrying(false);
      setError(false);
    }
  };

  // Reset and clean up when URL changes
  useEffect(() => {
    const urlChanged = previousUrlRef.current !== url;

    if (urlChanged && previousUrlRef.current) {
      // Clean up previous connection
      if (webrtcReaderRef.current) {
        webrtcReaderRef.current.close();
        webrtcReaderRef.current = null;
      }

      // Reset video element
      if (videoRef.current) {
        const currentStream = videoRef.current.srcObject as MediaStream | null;
        if (currentStream) {
          // Stop all tracks before clearing
          currentStream.getTracks().forEach((track) => {
            track.stop();
          });
        }
        videoRef.current.srcObject = null;
        videoRef.current.src = '';
      }

      // Reset stream tracking
      currentStreamIdRef.current = null;

      // Reset states
      setIsRetrying(false);
      setError(false);
      setIsLoading(true);
      isRetryingRef.current = false;
      isLoadingRef.current = true;
    }

    previousUrlRef.current = url;
  }, [url]);

  // WHEP connection setup
  useEffect(() => {
    if (!url) {
      return;
    }

    let isMounted = true;
    let rafId: number | null = null;

    const startConnection = () => {
      if (!videoRef.current) {
        setTimeout(() => {
          if (!videoRef.current && isMounted) {
            setError(true);
            setIsLoading(false);
          } else if (videoRef.current && isMounted) {
            startConnection();
          }
        }, 100);
        return;
      }

      try {
        setIsLoading(true);
        setError(false);

        // Clean up any existing reader
        if (webrtcReaderRef.current) {
          webrtcReaderRef.current.close();
          webrtcReaderRef.current = null;
        }

        // Create new WebRTC reader
        const reader = new SiflyWebRTCReader({
          url,
          onTrack: (event) => {
            if (!isMounted || !videoRef.current) return;

            const stream = event.streams[0];
            if (stream && videoRef.current) {
              const newStreamId = stream.id;
              const currentStreamId = currentStreamIdRef.current;

              // If this is a new stream (different ID), we need to replace the old one
              if (currentStreamId !== null && currentStreamId !== newStreamId) {
                // Clear the old stream first to ensure clean state
                const currentStream = videoRef.current.srcObject as MediaStream | null;
                if (currentStream) {
                  // Stop all tracks in the old stream
                  currentStream.getTracks().forEach((track) => {
                    track.stop();
                  });
                }
                videoRef.current.srcObject = null;
              }

              // Update the current stream ID reference
              currentStreamIdRef.current = newStreamId;

              // Set the new stream
              videoRef.current.srcObject = stream;

              // Ensure video is playing
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise.catch((err) => {
                  // Ignore benign play interruption errors
                  if (err.name === 'AbortError' || err.message.includes('removed from the document')) {
                    return;
                  }
                  if (isMounted) {
                    setError(true);
                    setIsLoading(false);
                    setIsRetrying(false);
                  }
                });
              }

              // Don't clear loading/retry states here immediately.
              // Wait for onPlaying/onLoadedData event to ensure video is actually visible (has keyframe).
              // This prevents showing a black screen while waiting for the first keyframe.
            }
          },
          onError: (errorMessage) => {
            if (!isMounted) return;

            // Check if this is a retryable error (SiflyWebRTCReader handles retries internally)
            const isRetryableError = errorMessage.toLowerCase().includes('retrying');

            if (isRetryableError) {
              // Don't show error state during retry - keep video visible
              setIsRetrying(true);
              setIsLoading(true);
              setError(false);
              isRetryingRef.current = true;
              isLoadingRef.current = true;

              // Clear the stream ID reference so we accept the new stream when it arrives
              currentStreamIdRef.current = null;
            } else {
              // Fatal error - show error state
              setError(true);
              setIsLoading(false);
              setIsRetrying(false);
            }
          }
        });

        webrtcReaderRef.current = reader;
      } catch (err) {
        if (isMounted) {
          setError(true);
          setIsLoading(false);
        }
      }
    };

    // Use double RAF to ensure React has finished rendering and refs are attached
    rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          startConnection();
        }, 50);
      });
    });

    return () => {
      isMounted = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (webrtcReaderRef.current) {
        webrtcReaderRef.current.close();
        webrtcReaderRef.current = null;
      }
    };
  }, [url]);

  // Detect picture-in-picture mode
  useEffect(() => {
    const checkPipStatus = () => {
      setIsInPip(!!document.pictureInPictureElement);
    };

    checkPipStatus();
    document.addEventListener('enterpictureinpicture', checkPipStatus);
    document.addEventListener('leavepictureinpicture', checkPipStatus);

    return () => {
      document.removeEventListener('enterpictureinpicture', checkPipStatus);
      document.removeEventListener('leavepictureinpicture', checkPipStatus);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (webrtcReaderRef.current) {
        webrtcReaderRef.current.close();
        webrtcReaderRef.current = null;
      }

      if (videoRef.current) {
        const currentStream = videoRef.current.srcObject as MediaStream | null;
        if (currentStream) {
          // Stop all tracks before clearing
          currentStream.getTracks().forEach((track) => {
            track.stop();
          });
        }
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        videoRef.current.src = '';
        videoRef.current.load();
      }
      currentStreamIdRef.current = null;
    };
  }, []);

  // Only show error state for fatal errors, not during retries
  if (error && !isRetrying) {
    return (
      <Box
        className={`flex items-center justify-center ${className}`}
        sx={{
          backgroundColor: 'black',
          width,
          height
        }}
      >
        <Typography level='body-sm' color='danger'>
          No video available, checking video server...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={`relative ${className}`}
      onMouseEnter={() => setShowScaleControls(true)}
      onMouseLeave={() => setShowScaleControls(false)}
      sx={{
        width,
        height
      }}
    >
      <VideoScaler
        fitMode={fitMode}
        aspectRatio={16 / 9}
        backgroundColor='#515359'
        videoRef={videoRef}
        videoProps={{
          autoPlay: true,
          muted: true,
          playsInline: true,
          controls: false,
          onPlaying: handleVideoPlaying,
          onLoadedData: handleVideoPlaying,
          className: 'bg-[#515359]',
          style: {
            pointerEvents: 'none',
            opacity: isLoading && !isRetrying ? 0 : 1
          }
        }}
        isInPip={inPipMode}
      />
      {(isLoading || isRetrying) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backgroundColor: isRetrying ? 'rgba(0, 0, 0, 0.5)' : 'transparent'
          }}
        >
          <Typography level='body-sm' sx={{ color: 'white' }}>
            {isRetrying ? 'Reconnecting stream...' : 'Loading stream...'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SimpleVideoStream;
